<template>
  <div class="graph-canvas-wrapper">
    <div ref="graphContainer" class="graph-container"></div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { Graph, Shape, Node } from '@antv/x6'
import { Export } from '@antv/x6-plugin-export'
import { GameNode, ProcessConfigV2, PlotEnding } from '@/api/node'

// 节点卡片尺寸
const NODE_WIDTH = 280
const HEADER_HEIGHT = 42       // .gn-header: padding 10+10 + line ~20 + border 1 + 1
const COND_HEIGHT = 28         // .gn-cond: padding 5+5 + line ~16 + border 1 + 1
const SLOTS_PAD_TOP = 8        // .gn-slots padding-top
const SLOT_HEIGHT = 24         // .gn-slot height
const EMPTY_HEIGHT = 32        // .gn-empty height
const BE_FLAG_HEIGHT = 26      // .gn-be-flag: padding 5+5 + line ~16

// 进度指标颜色调色板
const SLOT_COLORS = [
  '#409EFF', // 蓝
  '#67C23A', // 绿
  '#E6A23C', // 橙
  '#F56C6C', // 红
  '#9B59B6', // 紫
  '#00BCD4', // 青
  '#FF9800', // 深橙
  '#E91E63', // 粉
]

function getSlotColor(index: number): string {
  return SLOT_COLORS[index % SLOT_COLORS.length]
}

// 获取 AI 节点的结局列表（兼容旧 string 格式）
function getPlotEndings(nodeData: GameNode): PlotEnding[] {
  if (!nodeData.current_plot_new) return []
  const pe = nodeData.current_plot_new.plot_end
  if (Array.isArray(pe)) return pe
  if (typeof pe === 'string' && pe.trim()) {
    return [{ name: '结局1', content: pe, output_examples: [] }]
  }
  return []
}

// 根据节点数据生成 HTML 字符串
function buildNodeHtml(nodeData: GameNode, connections: Record<number, number>, isRoot = true): string {
  const isAi = nodeData.node_type === 'ai'
  const endings = isAi ? getPlotEndings(nodeData) : []
  const badgeClass = isAi ? 'badge-ai' : 'badge-fixed'
  const badgeText = isAi ? 'AI' : '固定'

  const cursorStyle = isRoot ? 'cursor: grab' : 'cursor: default'
  const typeClass = isAi ? 'card-ai' : 'card-fixed'
  const beClass = nodeData.is_be ? ' card-be' : ''
  let html = `<div class="graph-node-card ${typeClass}${beClass}" style="${cursorStyle}">`

  // Header
  html += `<div class="gn-header">
    <span class="gn-badge ${badgeClass}">${badgeText}</span>
    <span class="gn-title">${nodeData.title || '未命名节点'}</span>
    <span class="gn-id">#${nodeData.id || '?'}</span>
  </div>`

  // Entry conditions
  if (nodeData.entry_conditions && nodeData.entry_conditions.length > 0) {
    const condText = nodeData.entry_conditions.map(c => c.type).join(', ')
    html += `<div class="gn-cond">🔒 ${condText}</div>`
  }

  // AI 节点：按结局显示槽
  if (isAi && endings.length > 0) {
    html += `<div class="gn-slots">`
    endings.forEach((ending, idx) => {
      const targetId = connections[idx]
      const targetText = targetId ? `→ #${targetId}` : ''
      const color = getSlotColor(idx)
      const linkedClass = targetId ? 'slot-linked' : 'slot-unlinked'
      html += `<div class="gn-slot ${linkedClass}">
        <span class="slot-dot" style="background:${targetId ? color : '#c0c4cc'};${targetId ? `box-shadow:0 0 4px ${color}60` : ''}"></span>
        <span class="slot-name" style="color:${color}">${ending.name || '未命名结局'}</span>
        ${targetText ? `<span class="slot-target" style="color:${color}">${targetText}</span>` : ''}
      </div>`
    })
    html += `</div>`
  } else if (!isAi) {
    // 固定节点：显示"下一节点"槽
    const targetId = connections[0]
    const targetText = targetId ? `→ #${targetId}` : ''
    const color = getSlotColor(0)
    const linkedClass = targetId ? 'slot-linked' : 'slot-unlinked'
    html += `<div class="gn-slots">`
    html += `<div class="gn-slot ${linkedClass}">
      <span class="slot-dot" style="background:${targetId ? color : '#c0c4cc'};${targetId ? `box-shadow:0 0 4px ${color}60` : ''}"></span>
      <span class="slot-name" style="color:${color}">下一节点</span>
      ${targetText ? `<span class="slot-target" style="color:${color}">${targetText}</span>` : ''}
    </div>`
    html += `</div>`
  } else {
    html += `<div class="gn-empty">无剧情结局</div>`
  }

  // BE 结局标记
  if (nodeData.is_be) {
    html += `<div class="gn-be-flag">💀 BE 结局</div>`
  }

  html += `</div>`
  return html
}

// 计算节点的输出槽数量（AI节点=结局数，固定节点=1个"下一节点"槽）
function getOutputSlotCount(nodeData: GameNode): number {
  if (nodeData.node_type === 'ai') {
    return getPlotEndings(nodeData).length
  }
  return 1 // 固定节点有一个"下一节点"槽
}

// 计算节点高度
function calcNodeHeight(nodeData: GameNode): number {
  const hasConditions = nodeData.entry_conditions && nodeData.entry_conditions.length > 0
  const slotCount = getOutputSlotCount(nodeData)
  let h = HEADER_HEIGHT
  if (hasConditions) h += COND_HEIGHT
  if (slotCount > 0) {
    h += SLOTS_PAD_TOP + slotCount * SLOT_HEIGHT + SLOTS_PAD_TOP
  } else {
    h += EMPTY_HEIGHT
  }
  if (nodeData.is_be) h += BE_FLAG_HEIGHT
  return h
}

// 从上到下布局：输出端口在卡片底边横向均匀分布，计算第 idx 个端口的 X 坐标
function calcPortX(slotCount: number, index: number): number {
  return (NODE_WIDTH * (index + 1)) / (slotCount + 1)
}

let shapeRegistered = false

@Component
export default class GraphCanvas extends Vue {
  @Prop({ default: () => [] }) readonly nodes!: GameNode[]
  @Prop({ default: () => ({}) }) readonly positions!: Record<number, { x: number; y: number }>
  @Prop({ default: () => ({}) }) readonly connections!: Record<number, Record<number, number>>
  @Prop({ default: () => new Set() }) readonly rootNodeIds!: Set<number>

  graph: Graph | null = null
  private renderTimer: number | null = null
  // 点击连线状态
  private pendingSource: { nodeId: number; portId: string } | null = null

  mounted() {
    this.registerShapes()
    this.initGraph()
    this.scheduleRender()
    document.addEventListener('keydown', this.onKeyDown)
  }

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeyDown)
    if (this.renderTimer != null) {
      clearTimeout(this.renderTimer)
      this.renderTimer = null
    }
    if (this.graph) {
      this.graph.dispose()
      this.graph = null
    }
  }

  private onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.pendingSource) {
      this.cancelPendingConnection()
    }
  }

  private cancelPendingConnection() {
    if (!this.pendingSource || !this.graph) return
    const srcNode = this.graph.getCellById(String(this.pendingSource.nodeId))
    if (srcNode && srcNode.isNode()) {
      (srcNode as Node).setPortProp(this.pendingSource.portId, 'attrs/circle/fill', '#fff');
      (srcNode as Node).setPortProp(this.pendingSource.portId, 'attrs/circle/r', 6)
    }
    this.pendingSource = null
  }

  @Watch('nodes')
  onNodesChange() {
    this.scheduleRender()
  }

  @Watch('connections', { deep: true })
  onConnectionsChange() {
    this.scheduleRender()
  }

  @Watch('rootNodeIds')
  onRootNodeIdsChange() {
    this.scheduleRender()
  }

  // 防抖：多个 watcher 同时触发时只渲染一次
  // 使用 setTimeout 替代 $nextTick，避免跨 tick 的多次渲染
  scheduleRender() {
    if (this.renderTimer != null) {
      clearTimeout(this.renderTimer)
    }
    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null
      this.renderGraph()
    }, 0)
  }

  registerShapes() {
    if (shapeRegistered) return
    shapeRegistered = true

    Shape.HTML.register({
      shape: 'game-node',
      width: NODE_WIDTH,
      height: 100,
      html(cell) {
        const data = cell.getData() as { node: GameNode; connections: Record<number, number>; isRoot?: boolean }
        if (!data) {
          const div = document.createElement('div')
          div.innerHTML = '<div class="graph-node-card"><div class="gn-header"><span class="gn-title">Loading...</span></div></div>'
          return div
        }
        const div = document.createElement('div')
        div.innerHTML = buildNodeHtml(data.node, data.connections || {}, data.isRoot !== false)
        return div
      },
    })
  }

  initGraph() {
    const container = this.$refs.graphContainer as HTMLElement
    if (!container) return

    this.graph = new Graph({
      container,
      autoResize: true,
      panning: {
        enabled: true,
        eventTypes: ['leftMouseDown', 'mouseWheel'],
      },
      mousewheel: {
        enabled: true,
        modifiers: ['ctrl', 'meta'],
      },
      grid: {
        visible: true,
        size: 15,
        type: 'dot',
      },
      background: {
        color: '#f9fafb',
      },
      connecting: {
        router: 'manhattan',
        connector: {
          name: 'rounded',
          args: { radius: 8 },
        },
      },
      interacting: (cellView) => {
        const nodeId = Number(cellView.cell.id)
        return { nodeMovable: this.rootNodeIds.has(nodeId) }
      },
    })

    // 导出插件（用于导出整张图谱为 SVG）
    this.graph.use(new Export())

    // 双击节点 -> 编辑
    this.graph.on('node:dblclick', ({ node }) => {
      const nodeId = Number(node.id)
      this.$emit('node-dblclick', nodeId)
    })

    // 节点拖拽过程中实时 emit（用于子树跟随）
    this.graph.on('node:change:position', ({ node, current }) => {
      if (current) {
        const nodeId = Number(node.id)
        this.$emit('node-moving', nodeId, current.x, current.y)
      }
    })

    // 节点移动完成 -> 保存位置
    this.graph.on('node:moved', ({ node }) => {
      const nodeId = Number(node.id)
      const pos = node.getPosition()
      this.$emit('node-moved', nodeId, pos.x, pos.y)
    })

    // 点击连线：点击输出端口开始，点击目标节点完成
    this.graph.on('node:click', ({ e, node }) => {
      // 检测是否点击了端口
      const target = e.target as SVGElement
      const portEl = target.closest('[port]') as SVGElement | null
      const portId = portEl ? portEl.getAttribute('port') : null

      if (portId && portId.startsWith('process-')) {
        // 点击了输出端口
        if (this.pendingSource) {
          this.cancelPendingConnection()
        }
        const nodeId = Number(node.id)
        this.pendingSource = { nodeId, portId }
        // 高亮源端口
        const color = getSlotColor(parseInt(portId.replace('process-', '')))
        node.setPortProp(portId, 'attrs/circle/fill', color)
        node.setPortProp(portId, 'attrs/circle/r', 8)
      } else if (this.pendingSource) {
        // 有待连接状态，点击了目标节点 -> 完成连线
        const targetNodeId = Number(node.id)
        if (targetNodeId === this.pendingSource.nodeId) return // 禁止自环
        const processIndex = parseInt(this.pendingSource.portId.replace('process-', ''))
        if (isNaN(processIndex)) return
        const srcNodeId = this.pendingSource.nodeId
        this.cancelPendingConnection()
        this.$emit('edge-created', srcNodeId, processIndex, targetNodeId)
      } else {
        // 普通点击节点 -> 弹出节点详情
        this.$emit('node-click', Number(node.id), e)
      }
    })

    // 点击空白区域 -> 取消连线 + 关闭详情弹层
    this.graph.on('blank:click', () => {
      if (this.pendingSource) {
        this.cancelPendingConnection()
      }
      this.$emit('blank-click')
    })

    // 右键菜单
    this.graph.on('edge:contextmenu', ({ e, edge }) => {
      e.preventDefault()
      e.stopPropagation()
      this.$emit('edge-contextmenu', edge, e)
    })

    this.graph.on('node:contextmenu', ({ e, node }: { e: MouseEvent; node: any }) => {
      e.preventDefault()
      e.stopPropagation()
      this.$emit('node-contextmenu', Number(node.id), e)
    })
  }

  renderGraph() {
    if (!this.graph) return

    // 先逐个移除所有 cell，确保 HTML shape 的 DOM 被正确清理
    const cells = this.graph.getCells()
    if (cells.length > 0) {
      this.graph.removeCells(cells)
    }
    this.graph.clearCells()

    // 创建节点
    for (const node of this.nodes) {
      if (!node.id) continue

      const isAi = node.node_type === 'ai'
      const endings = isAi ? getPlotEndings(node) : []
      const nodeConns = this.connections[node.id] || {}
      const height = calcNodeHeight(node)
      const pos = this.positions[node.id] || { x: 100, y: 100 }

      // 从上到下布局：input 端口固定在顶边中央；两侧再各加一个隐形入口端口，
      // 供"跨越式/回跳"连线从侧面进入，避免与主链在顶部挤成一条重叠线
      const inputPortConfig: any = { id: 'input', group: 'input' }
      const sideY = Math.round(height / 2)

      // 端口配置：output 端口沿底边横向均匀分布，每个端口独立颜色
      const portItems: any[] = [
        inputPortConfig,
        { id: 'input-left', group: 'inputSide', args: { x: 0, y: sideY } },
        { id: 'input-right', group: 'inputSide', args: { x: NODE_WIDTH, y: sideY } },
      ]
      if (isAi && endings.length > 0) {
        // AI 节点：按结局在底边创建输出端口
        endings.forEach((_ending, idx) => {
          const portX = calcPortX(endings.length, idx)
          const color = getSlotColor(idx)
          portItems.push({
            id: `process-${idx}`,
            group: 'output',
            args: { x: portX, y: height },
            attrs: {
              circle: {
                stroke: color,
                fill: '#fff',
              },
            },
          })
        })
      } else if (!isAi) {
        // 固定节点：底边中央添加一个"下一节点"输出端口
        const portX = calcPortX(1, 0)
        const color = getSlotColor(0)
        portItems.push({
          id: 'process-0',
          group: 'output',
          args: { x: portX, y: height },
          attrs: {
            circle: {
              stroke: color,
              fill: '#fff',
            },
          },
        })
      }

      this.graph!.addNode({
        id: String(node.id),
        shape: 'game-node',
        x: pos.x,
        y: pos.y,
        width: NODE_WIDTH,
        height,
        data: { node, connections: nodeConns, isRoot: this.rootNodeIds.has(node.id!) },
        ports: {
          groups: {
            input: {
              position: 'top',
              attrs: {
                circle: {
                  r: 6,
                  magnet: false,
                  stroke: '#67C23A',
                  fill: '#fff',
                  strokeWidth: 2,
                  style: { cursor: 'pointer' },
                },
              },
            },
            inputSide: {
              position: 'absolute',
              attrs: {
                circle: {
                  r: 0,
                  magnet: false,
                  fill: 'transparent',
                  stroke: 'transparent',
                },
              },
            },
            output: {
              position: 'absolute',
              attrs: {
                circle: {
                  r: 6,
                  magnet: false,
                  stroke: '#409EFF',
                  fill: '#fff',
                  strokeWidth: 2,
                  style: { cursor: 'crosshair' },
                },
              },
            },
          },
          items: portItems,
        },
      })
    }

    // 创建连线（从 connections 推导）
    for (const srcIdStr of Object.keys(this.connections)) {
      const srcId = Number(srcIdStr)
      const conns = this.connections[srcId]
      for (const pIdxStr of Object.keys(conns)) {
        const pIdx = Number(pIdxStr)
        const targetId = conns[pIdx]

        // 确保源和目标节点都存在
        const srcNode = this.nodes.find(n => n.id === srcId)
        const tgtNode = this.nodes.find(n => n.id === targetId)
        if (!srcNode || !tgtNode) continue

        // AI 节点用结局名称，固定节点无标签
        const srcEndings = srcNode.node_type === 'ai' ? getPlotEndings(srcNode) : []
        const edgeLabel = srcEndings[pIdx]?.name || ''
        const edgeColor = getSlotColor(pIdx)

        // 根据源/目标的相对位置决定目标进入口，避免多条线在顶部挤成一条重叠线：
        // - 目标在源正下方且横向重叠 => 从顶部进入（主链正常向下）
        // - 否则（跨列/跨越式跳转）=> 从目标侧面进入，走独立的侧向车道
        const srcPos = this.positions[srcId] || { x: 100, y: 100 }
        const tgtPos = this.positions[targetId] || { x: 100, y: 100 }
        const srcBottom = srcPos.y + calcNodeHeight(srcNode)
        const srcCx = srcPos.x + NODE_WIDTH / 2
        const tgtCx = tgtPos.x + NODE_WIDTH / 2
        const overlapX = srcPos.x < tgtPos.x + NODE_WIDTH && tgtPos.x < srcPos.x + NODE_WIDTH
        let targetPort = 'input'
        let endDir: 'top' | 'left' | 'right' = 'top'
        if (tgtPos.y >= srcBottom - 20 && overlapX) {
          targetPort = 'input'
          endDir = 'top'
        } else if (tgtCx >= srcCx) {
          targetPort = 'input-left'
          endDir = 'left'
        } else {
          targetPort = 'input-right'
          endDir = 'right'
        }

        // 侧向跳线：像标准流程图那样"先直下、到目标那一行再横拐"进入目标侧面。
        // 用显式拐点（源出口正下方 → 目标中线高度）取代自动路由的"先横后纵"。
        let routerConfig: any
        let vertices: Array<{ x: number; y: number }> = []
        if (endDir === 'top') {
          routerConfig = {
            name: 'manhattan',
            args: { padding: 20, startDirections: ['bottom'], endDirections: ['top'] },
          }
        } else {
          const srcSlotCount = srcNode.node_type === 'ai' ? Math.max(srcEndings.length, 1) : 1
          const srcPortIdx = srcNode.node_type === 'ai' ? pIdx : 0
          const srcOutX = srcPos.x + calcPortX(srcSlotCount, srcPortIdx)
          const targetSideY = tgtPos.y + calcNodeHeight(tgtNode) / 2
          vertices = [{ x: srcOutX, y: targetSideY }]
          routerConfig = { name: 'orth', args: { padding: 12 } }
        }

        this.graph!.addEdge({
          source: { cell: String(srcId), port: `process-${pIdx}` },
          target: { cell: String(targetId), port: targetPort },
          vertices,
          router: routerConfig,
          connector: {
            name: 'rounded',
            args: { radius: 8 },
          },
          attrs: {
            line: {
              stroke: edgeColor,
              strokeWidth: 2,
              targetMarker: {
                name: 'classic',
                size: 8,
              },
            },
          },
          labels: edgeLabel
            ? [
                {
                  attrs: {
                    label: {
                      text: edgeLabel,
                      fontSize: 11,
                      fill: edgeColor,
                      fontWeight: 600,
                    },
                    rect: {
                      fill: '#ffffffcc',
                      stroke: 'none',
                      rx: 2,
                      ry: 2,
                    },
                  },
                  position: {
                    distance: 0.5,
                  },
                },
              ]
            : [],
          data: {
            sourceNodeId: srcId,
            processIndex: pIdx,
          },
        })
      }
    }
  }

  // Public methods
  zoomIn() {
    if (this.graph) {
      this.graph.zoom(0.1)
    }
  }

  zoomOut() {
    if (this.graph) {
      this.graph.zoom(-0.1)
    }
  }

  zoomFit() {
    if (this.graph) {
      this.graph.zoomToFit({ padding: 40, maxScale: 1.2 })
    }
  }

  resetView() {
    if (!this.graph) return
    this.graph.zoomTo(1)
    const nodes = this.graph.getNodes()
    if (nodes.length === 0) return
    let minX = Infinity
    let minY = Infinity
    for (const node of nodes) {
      const pos = node.getPosition()
      if (pos.x < minX) minX = pos.x
      if (pos.y < minY) minY = pos.y
    }
    this.graph.positionPoint({ x: minX - 40, y: minY - 40 }, 0, 0)
  }

  getGraph(): Graph | null {
    return this.graph
  }

  /** 将视口定位到指定节点（居中显示，重置为 100% 缩放） */
  focusNode(nodeId: number, scale = 1) {
    if (!this.graph) return
    const cell = this.graph.getCellById(String(nodeId))
    if (!cell || !cell.isNode()) return
    this.graph.zoomTo(scale)
    this.graph.centerCell(cell)
  }

  // 导出整张图谱为 SVG（含所有节点与跳转连线）
  exportSVG(fileName: string) {
    if (!this.graph) return
    // 先适应视图，保证导出内容包含全部节点
    this.graph.zoomToFit({ padding: 40, maxScale: 1.2 })
    ;(this.graph as any).exportSVG(fileName, {
      padding: 40,
      copyStyles: true,
    })
  }
}
</script>

<style lang="scss">
.graph-canvas-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;

  .graph-container {
    width: 100%;
    height: 100%;
  }
}

/* 节点卡片样式 - 不加 scoped，因为这些 DOM 元素由 X6 在外部渲染 */
.graph-node-card {
  background: #fff;
  border: 2px solid #d9dce1;
  border-radius: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-size: 13px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.graph-node-card:hover {
  border-color: #409EFF;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.2);
}

.gn-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
  border-bottom: 1px solid #ebeef5;
}

.gn-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}

.gn-badge.badge-ai {
  background: #1976d2;
  color: #fff;
  border: 1px solid #1565c0;
}

.gn-badge.badge-fixed {
  background: #78909c;
  color: #fff;
  border: 1px solid #607d8b;
}

/* ===== 节点类型：整个头部（上半部分）用底色区分 ===== */
/* AI 节点：蓝色头部 */
.graph-node-card.card-ai .gn-header {
  background: linear-gradient(135deg, #e3f0ff 0%, #cfe4ff 100%);
  border-bottom-color: #b3d4f5;
}
/* 固定节点：灰色头部 */
.graph-node-card.card-fixed .gn-header {
  background: linear-gradient(135deg, #f0f2f5 0%, #e2e6ea 100%);
  border-bottom-color: #d4d8dd;
}

/* ===== BE 结局节点：下半部分标红突出 ===== */
.graph-node-card.card-be {
  border-color: #f56c6c;
}
.graph-node-card.card-be .gn-slots,
.graph-node-card.card-be .gn-empty {
  background: #fde8e8;
  border-top: 1px solid #f8c9c9;
}
.gn-be-flag {
  padding: 5px 14px;
  background: #f56c6c;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 1px;
}

.gn-title {
  flex: 1;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.gn-id {
  color: #909399;
  font-size: 12px;
  flex-shrink: 0;
  font-weight: 500;
}

.gn-cond {
  padding: 5px 14px;
  font-size: 11px;
  color: #e6a23c;
  background: #fdf6ec;
  border-bottom: 1px solid #f5deb3;
}

.gn-slots {
  padding: 8px 14px;
}

.gn-slot {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 24px;
  font-size: 12px;
  padding: 0 2px;
}

.gn-slot .slot-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.gn-slot.slot-linked .slot-dot {
  background: #409EFF;
  box-shadow: 0 0 4px rgba(64, 158, 255, 0.4);
}

.gn-slot.slot-unlinked .slot-dot {
  background: #c0c4cc;
}

.gn-slot .slot-name {
  color: #303133;
  font-weight: 500;
}

.gn-slot .slot-range {
  color: #909399;
  font-size: 11px;
}

.gn-slot .slot-target {
  margin-left: auto;
  color: #409EFF;
  font-size: 11px;
  font-weight: 500;
}

.gn-empty {
  padding: 8px 14px;
  color: #c0c4cc;
  font-size: 12px;
  text-align: center;
  font-style: italic;
}

/* 端口交互样式 */
.x6-port-body {
  cursor: crosshair;
  transition: r 0.15s, fill 0.15s;
}
</style>
